import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import MarkEmailRead from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnread from "@mui/icons-material/MarkEmailUnread";
import PublicIcon from "@mui/icons-material/Public";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { green, lightBlue } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import parse from "html-react-parser";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import {
  getIndividualByUserId,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import MessagingAPIService from "messaging/_api/messaging-api-service";
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { addComment, removeComment } from "work/proposals/comments/_store/comments-redux-slice";
import Comment from "work/proposals/comments/comment";
import { addCommentThread } from "work/proposals/comments/threads/_store/comment-thread-redux-slice";

const SystemCommentContainer = styled("div")(({ theme }) => ({
  "@container (min-width: 800px)": {
    width: "75%",
  },
  "@container (min-width: 0px)": {
    width: "100%",
  },
  margin: theme.spacing(0, "auto", 4, "auto"),
  whiteSpace: "normal",
  width: "80%",
}));
const SystemCommentInfo = styled("div")(({ theme }) => ({
  alignItems: "start",
  display: "flex",
  justifyContent: "space-between",
  marginBottom: theme.spacing(0.5),
}));
const SystemCommentTime = styled(Typography)(({ theme }) => ({
  fontSize: "0.8em",
  marginLeft: theme.spacing(1.32),
}));
const EditCommentInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper,
    "& fieldset": {
      borderColor: theme.palette.common.black,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.common.black,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.common.black,
    },
  },
}));
const CommentListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== "pending" && prop !== "unread",
})<{ pending: boolean; unread: boolean }>(({ pending, unread }) => ({
  opacity: pending ? 0.66 : 1,

  "& .MuiTypography-root": {
    fontWeight: unread ? 500 : "normal",
  },
}));
const NameLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled: boolean }>(({ disabled }) => ({
  display: "inline-block",
  cursor: disabled ? "default" : "pointer",
  textDecoration: disabled ? "none" : "underline",
}));
const NameContainer = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(1),
}));
const StatusIndicators = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(0.5),
}));
const StatusDot = styled("span", {
  shouldForwardProp: (prop) => prop !== "color" && prop !== "alert",
})<{ color: "pending" | "unread"; alert?: "error" }>(({ theme, color, alert }) => ({
  backgroundColor:
    color === "pending"
      ? alert === "error"
        ? theme.palette.error.main
        : theme.palette.warning.main
      : theme.palette.secondary.main,
  borderRadius: "50%",
  display: "inline-block",
  height: "8px",
  width: "8px",
}));
const CommentDate = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "block",
}));
const ExternalChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "pending",
})<{ pending?: boolean }>(({ theme, pending }) => ({
  border: 0,
  marginLeft: theme.spacing(0.5),
  color: pending ? theme.palette.error.main : undefined,
  "& .MuiChip-icon": {
    color: pending ? theme.palette.error.main : undefined,
  },
}));
const PendingChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "isexternal",
})<{ isexternal?: boolean }>(({ theme, isexternal }) => ({
  border: isexternal ? `1px solid ${theme.palette.error.main}` : 0,
  ...(isexternal
    ? {
        color: theme.palette.error.main,
        borderColor: theme.palette.error.main,
      }
    : {}),
}));
const CommentContent = styled("span")(({ theme }) => ({
  whiteSpace: "normal",
  width: "100%",
  "& span.action": {
    fontWeight: 500,
    "&.accepted": {
      color: theme.palette.success.main,
    },
    "&.rejected": {
      color: theme.palette.error.main,
    },
  },
  "& > span.comment-redline": {
    width: "100%",
    "& > span.change-group": {
      whiteSpace: "nowrap",
      "& .added": {
        "&.resolved": {
          color: theme.palette.text.primary,
        },
        backgroundColor: "unset",
        color: theme.palette.text.primary,
      },
    },
    "& span.added": {
      backgroundColor: green[300],
      color: theme.palette.text.primary,
      "&.resolved": {
        backgroundColor: "unset",
        color: lightBlue[700],
      },
    },
    "& span.removed": {
      textDecoration: "line-through",
      textDecorationColor: theme.palette.error.main,
      "&.resolved": {
        textDecorationColor: lightBlue[700],
      },
    },
    "& span.unchanged": {
      color: theme.palette.text.primary,
    },
  },
}));
const SystemMessageContent = styled("div")(({ theme }) => ({
  backgroundColor: "#e0e0e0",
  borderRadius: theme.spacing(0.66),
  display: "block",
  textAlign: "center",
  padding: theme.spacing(2),
  "& > p": {
    margin: 0,
  },
}));

type CommentItemProps = {
  className?: string;
  comment: Comment;
  audienceSwitch?: React.ReactNode;
  readOnly?: boolean;
};

const userIdRegEx = new RegExp(
  /userId:\s?([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})/
);

export default function CommentItem(props: Readonly<CommentItemProps>) {
  const { className, comment, audienceSwitch, readOnly = false } = props;

  const [isBeingEdited, setIsBeingEdited] = React.useState<boolean>(false);
  const [editedCommentText, setEditedCommentText] = React.useState<string>("");
  const [isUpdatingReadStatus, setIsUpdatingReadStatus] = React.useState(false);
  const commenter = useSelector(getIndividualByUserId(comment.senderId ?? undefined));

  const { openDialog } = useDialog();
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const commentUserId: Guid | undefined = useMemo(() => {
    const userIdMatch = userIdRegEx.exec(comment.content);
    if (!userIdMatch) return;
    return new Guid(userIdMatch[1]);
  }, [comment.content]);
  const commentUser = useSelector(getIndividualByUserId(commentUserId));

  const individualUserIds = new Set([
    ...(comment.senderId ? [comment.senderId.value] : []),
    ...(commentUserId ? [commentUserId.value] : []),
  ]);
  useGetIndividualsByUserIdsQuery(
    { userIds: individualUserIds },
    { skip: individualUserIds.size === 0 }
  );

  function getCommentContent(comment: Comment) {
    if (comment.isDeleted && !comment.markedForDeletion) {
      return <i>This comment was deleted</i>;
    }

    let content: string = comment.content;

    if (commentUser && comment.content) {
      content = content.replace(userIdRegEx, commentUser.fullName);
    }
    return <CommentContent>{parse(content)}</CommentContent>;
  }

  function handleViewCommenterProfile() {
    if (!commenter) {
      console.warn("Commenter not found");
      return;
    }

    openDialog({
      component: <ViewIndividualProfile profile={commenter.profile} />,
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
  }

  const isOwnComment = Boolean(comment.senderId?.isEqualTo(session.user?.id));
  const isUnread = Boolean(
    (!isOwnComment &&
      !comment.markedForReadReceipt &&
      !comment.isReadByUser(session.user?.id)) ||
      comment.markedForUnread
  );
  const showPendingIndicator = comment.isPending;
  const isPendingExternalComment = Boolean(comment.isPending && comment.thread.isExternal);

  async function handleReadStatusChanged(updatedComment: Comment) {
    if (!updatedComment.id || isUpdatingReadStatus) return;

    setIsUpdatingReadStatus(true);
    try {
      const apiService = new MessagingAPIService(session);
      const returnedMessages = await apiService.createBulkMessages([
        updatedComment.toMessage({
          isViewingAsVendor: session.context?.viewingAsVendor ?? false,
        }),
      ]);
      if (returnedMessages.length < 1) {
        console.error(readOnly ? "Failed to mark comment as read." : "Failed to update comment read status.");
        return;
      }
      const returnedComment = Comment.fromMessage(returnedMessages[0]);
      dispatch(addComment(returnedComment));
      dispatch(addCommentThread(returnedComment.thread));
    } catch (error) {
      console.error(readOnly ? "Error marking comment as read:" : "Error updating comment read status:", error);
    } finally {
      setIsUpdatingReadStatus(false);
    }
  }

  function renderReadUnreadButton(comment: Comment): React.ReactNode {
    if (readOnly && !isUnread) {
      return null;
    }

    const title = readOnly || isUnread ? "Mark Read" : "Mark Unread";
    const updatedComment = isUnread ? comment.markForReadReceipt() : comment.markForUnread();

    return (
      <Tooltip title={title}>
        <IconButton
          aria-label={title}
          color="primary"
          disabled={isUpdatingReadStatus}
          size="small"
          style={{ alignContent: "right", float: "right" }}
          onClick={() => void handleReadStatusChanged(updatedComment)}
        >
          {readOnly ? (
            <MarkEmailRead fontSize="inherit" />
          ) : isUnread ? (
            <MarkEmailUnread fontSize="inherit" />
          ) : (
            <MarkEmailRead fontSize="inherit" />
          )}
        </IconButton>
      </Tooltip>
    );
  }

  return comment.senderId ? (
    <CommentListItem
      className={className}
      key={comment.id?.value ?? Guid.generate().value}
      pending={comment.isPending}
      unread={isUnread}
      disableGutters
      alignItems="flex-start"
    >
      <ListItemAvatar>
        <IndividualAvatar individual={commenter ?? undefined} />
      </ListItemAvatar>
      {!readOnly && isBeingEdited && (
        <EditCommentInput
          multiline
          rows={4}
          variant="outlined"
          fullWidth
          value={editedCommentText}
          helperText={audienceSwitch}
          slotProps={{
            input: {
              endAdornment: (
                <>
                  <Tooltip title="Save comment">
                <IconButton
                  aria-label="Save comment"
                  color="primary"
                  size="small"
                  disabled={editedCommentText.trim() === ""}
                      onClick={() => {
                        if (editedCommentText.trim() === "") return;
                        dispatch(addComment(comment.edit(editedCommentText)));
                        setIsBeingEdited(false);
                      }}
                    >
                      <SaveIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel editing">
                    <IconButton
                      aria-label="Cancel editing"
                      color="primary"
                      size="small"
                      onClick={() => {
                        setIsBeingEdited(false);
                        setEditedCommentText("");
                      }}
                    >
                      <CancelIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </>
              ),
            },
          }}
          onKeyDown={(event) => {
            if (event.key === KeyboardKey.Enter && editedCommentText.trim() !== "") {
              event.preventDefault();
              let updatedComment = comment.edit(editedCommentText);
              updatedComment = updatedComment.markForEdit();
              dispatch(addComment(updatedComment));
            } else if (event.key === KeyboardKey.Escape) {
              setIsBeingEdited(false);
            }
          }}
          onChange={(event) => setEditedCommentText(event.target.value)}
        />
      )}
      {!isBeingEdited && comment.senderId && (
        <ListItemText
          primary={
            <>
              <NameContainer>
                {(showPendingIndicator || isUnread) && (
                  <StatusIndicators>
                    {showPendingIndicator && (
                      <StatusDot
                        aria-hidden="true"
                        color="pending"
                        alert={isPendingExternalComment ? "error" : undefined}
                        data-testid="pending-comment-dot"
                      />
                    )}
                    {isUnread && <StatusDot aria-hidden="true" color="unread" data-testid="unread-comment-dot" />}
                  </StatusIndicators>
                )}
                <NameLink
                  disabled={!commenter?.isVisible}
                  onClick={(event) => {
                    event.preventDefault();
                    if (!commenter?.isVisible) return;
                    handleViewCommenterProfile();
                  }}
                >
                  {commenter?.fullName}
                </NameLink>
              </NameContainer>
              {comment.thread.isExternal && (
                <ExternalChip
                  label="External"
                  size="small"
                  icon={<PublicIcon />}
                  pending={comment.isPending}
                  variant="outlined"
                  data-testid="external-comment-chip"
                />
              )}
              {comment.isPending && (
                <PendingChip
                  label={getPendingStatus(comment)}
                  size="small"
                  color={comment.thread.isExternal ? "error" : "warning"}
                  isexternal={comment.thread.isExternal}
                  variant="outlined"
                  data-testid="pending-comment-chip"
                />
              )}
              {!comment.updated && (
                <CommentDate variant="caption">
                  {comment.published
                    ? "Published: " + comment.published?.format("MMM D, YYYY h:mm A")
                    : new AHDate().format("MMM D, YYYY h:mm A")}
                </CommentDate>
              )}
              {comment.updated && (
                <CommentDate variant="caption">Edited: {comment.updated.format("MMM D, YYYY h:mm A")}</CommentDate>
              )}
            </>
          }
          secondary={getCommentContent(comment)}
          slotProps={{
            primary: {
              fontWeight: 500,
            },
            secondary: {
              color: "textPrimary",
            },
          }}
        />
      )}
      {comment.senderId && !comment.senderId.isEqualTo(session.user?.id) && renderReadUnreadButton(comment)}
      {!readOnly &&
        (!comment.isDeleted || comment.markedForDeletion) &&
        comment.senderId?.isEqualTo(session.user?.id) && (
        <>
          {!isBeingEdited && !comment.isAutoGenerated && !comment.isDeleted && (
            <>
              <Tooltip title="Edit comment">
                <IconButton
                  aria-label="Edit comment"
                  color="primary"
                  size="small"
                  onClick={() => {
                    setIsBeingEdited(true);
                    setEditedCommentText(comment.content ?? "");
                  }}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete comment">
                <IconButton
                  aria-label="Delete comment"
                  color="primary"
                  size="small"
                  onClick={() => {
                    if (comment.markedForCreation) {
                      dispatch(removeComment(comment));
                      return;
                    }
                    let updatedComment = comment.setDeleted();
                    updatedComment = updatedComment.markForDeletion();
                    dispatch(addComment(updatedComment));
                  }}
                >
                  <DeleteIcon color="error" fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {comment.isPending && comment.isDeleted && (
            <Tooltip title="Undo deletion">
              <IconButton
                aria-label="Undo deletion"
                color="primary"
                size="small"
                onClick={() => {
                  if (!comment.id) return;
                  let updatedComment = comment.setUndeleted();
                  dispatch(addComment(updatedComment));
                }}
              >
                <UndoIcon color="secondary" fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
    </CommentListItem>
  ) : (
    <SystemCommentContainer>
      {readOnly ? (
        <>
          <SystemCommentInfo style={{ justifyContent: "center" }}>
            <SystemCommentTime>
              <time>{comment.published?.format("MM/DD/YY hh:mm A")}</time>
            </SystemCommentTime>
          </SystemCommentInfo>
          <SystemMessageContent>
            <ReactMarkdown>{comment.content}</ReactMarkdown>
          </SystemMessageContent>
        </>
      ) : (
        <>
          <SystemMessageContent>
            <ReactMarkdown>{comment.content}</ReactMarkdown>
          </SystemMessageContent>
          <SystemCommentInfo>
            <SystemCommentTime>
              <time>{comment.published?.format("MM/DD/YY hh:mm A")}</time>
            </SystemCommentTime>
            {renderReadUnreadButton(comment)}
          </SystemCommentInfo>
        </>
      )}
    </SystemCommentContainer>
  );
}

function getPendingStatus(comment: Comment): string {
  if (comment.isDeleted) return "Pending Deletion";
  if (comment.markedForEdit) return "Pending Edit";
  return "Pending";
}
